import { apiClient } from './api';
import { tryRegisterJob } from './jobRegistry';

// Process a document to create AI embeddings with enhanced options for SOW documents
export const processDocument = async (documentId, metadata = {}) => {
  try {
    // For SOW documents, add specific SOW processing hints to the metadata
    if (metadata && metadata.document_type === 'SOW') {
      metadata = {
        ...metadata,
        enhanced_processing: true,
        extract_insights: true,
        generate_structured_data: true,
        sow_analysis: true,
        force_reprocess: true  // Force reprocessing even if already processed
      };
    }
    
    const response = await apiClient.post(`/ai/process-document/${documentId}`, { metadata });
    return response.data;
  } catch (error) {
    console.error('Error processing document:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to process document'
    };
  }
};

// Process all documents in a project with enhanced SOW handling
export const processProject = async (projectId) => {
  try {
    const response = await apiClient.post(`/ai/process-project/${projectId}`, {
      enhanced_sow_processing: true,
      reprocess_existing: true
    });
    return response.data;
  } catch (error) {
    console.error('Error processing project documents:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to process project documents'
    };
  }
};

// Query a document using AI with enhanced prompting
export const queryDocument = async (documentId, query) => {
  try {
    // Ensure query is a string
    const queryString = typeof query === 'string' ? query : String(query);
    
    // Enhance the query with additional context and formatting instructions
    const enhancedQuery = `
      Based on the content of this document, please answer the following question.
      Provide a comprehensive answer with specific details from the document.
      If lists are requested, format the response as a clear bullet-point or numbered list.
      
      Question: ${queryString}
    `;
    
    // Important: Only send the query as a string in the 'query' field
    const response = await apiClient.post(`/ai/query-document/${documentId}`, { 
      query: enhancedQuery
      // Remove the response_format so it doesn't get included in query string parsing
    });
    
    // Process the response to ensure answer is a string
    if (response.data && response.data.answer && typeof response.data.answer !== 'string') {
      try {
        response.data.answer = JSON.stringify(response.data.answer);
      } catch (jsonError) {
        console.warn('Failed to stringify answer object:', jsonError);
        response.data.answer = String(response.data.answer);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error querying document:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to query document'
    };
  }
};

// Query all documents in a project with enhanced prompting
export const queryProject = async (projectId, query) => {
  try {
    // Ensure query is a string
    const queryString = typeof query === 'string' ? query : String(query);
    
    // Add instructions for formatting to get more structured responses
    const enhancedQuery = `
      ${queryString}
      
      If your response contains a list of items, please format each item as a separate bullet point
      or numbered list item. Make each point specific, concise, and actionable.
      If you're extracting information like requirements, features, or tasks, make each one
      a separate list item rather than combining multiple items into a single bullet point.
      
      Important: Your response must be a text string. Do not return a JSON object or other complex data type.
    `;
    
    // Important: Only send the query string in the query field
    const response = await apiClient.post(`/ai/query-project/${projectId}`, { 
      query: enhancedQuery
      // Remove all additional properties from the request body to avoid query parsing issues
    });
    
    // Handle potential non-string responses
    if (response.data && response.data.answer && typeof response.data.answer !== 'string') {
      try {
        response.data.answer = JSON.stringify(response.data.answer);
      } catch (jsonError) {
        console.warn('Failed to stringify answer object:', jsonError);
        response.data.answer = String(response.data.answer);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error querying project:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to query project'
    };
  }
};

// Generate content from project documents
/**
 * Poll a background job until completed/failed.
 * Delegates to the global JobContext when available so jobs survive page navigation.
 * Falls back to local polling when the context is not mounted (e.g. during tests).
 */
const _pollJob = (jobId, label = 'AI Processing…', maxWaitMs = 600000, intervalMs = 2000) => {
  const globalPromise = tryRegisterJob(jobId, label);
  if (globalPromise) return globalPromise;

  // Fallback: local polling (context not yet mounted)
  const deadline = Date.now() + maxWaitMs;
  return new Promise((resolve, reject) => {
    const timer = setInterval(async () => {
      try {
        const res = await apiClient.get(`/jobs/${jobId}`);
        const job = res.data;
        if (job.status === 'completed') {
          clearInterval(timer);
          resolve(job.result);
        } else if (job.status === 'failed') {
          clearInterval(timer);
          reject(new Error(job.error_message || 'Job failed'));
        } else if (Date.now() > deadline) {
          clearInterval(timer);
          reject(new Error('Job timed out'));
        }
      } catch (err) {
        if (Date.now() > deadline) {
          clearInterval(timer);
          reject(new Error('Job timed out'));
        }
      }
    }, intervalMs);
  });
};

export const generateFromSOW = async (projectId, section) => {
  try {
    const sectionString = typeof section === 'string' ? section : String(section);
    const response = await apiClient.post(`/ai/generate-from-sow/${projectId}`, { section: sectionString });

    // Async path — backend returned a job_id
    if (response.data?.job_id) {
      const result = await _pollJob(response.data.job_id, `Generating ${sectionString} from project documents…`);
      if (result?.content && typeof result.content !== 'string') {
        try { result.content = JSON.stringify(result.content); } catch (_) { result.content = String(result.content); }
      }
      return result || { success: false, error: 'No result from job' };
    }

    // Legacy sync path
    if (response.data?.content && typeof response.data.content !== 'string') {
      try { response.data.content = JSON.stringify(response.data.content); } catch (_) { response.data.content = String(response.data.content); }
    }
    return response.data;
  } catch (error) {
    console.error('Error generating content from project documents:', error);
    return { success: false, error: error.message || 'Failed to generate content from project documents' };
  }
};

// Sync project documents to project-specific storage
export const syncProjectDocuments = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/sync-documents`);
    return response.data;
  } catch (error) {
    console.error('Error syncing project documents:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to sync project documents'
    };
  }
};

// Ensure SOW document is specifically processed with enhanced analysis
export const ensureSOWProcessed = async (projectId) => {
  try {
    const response = await apiClient.post(`/ai/ensure-sow-processed/${projectId}`);
    return response.data;
  } catch (error) {
    // This endpoint might not exist in current backend, handle gracefully
    console.warn('Error using ensure-sow-processed endpoint, falling back to process-project:', error);
    // Fallback to process-project
    return processProject(projectId);
  }
};

// Extract insights directly from project documents (simplified approach)
export const extractSOWInsights = async (projectId) => {
  try {
    const response = await apiClient.post(`/ai/extract-sow-insights/${projectId}`);
    if (response.data?.job_id) {
      const result = await _pollJob(response.data.job_id, 'Extracting project document insights…');
      return result || { success: false, error: 'No result from job' };
    }
    return response.data;
  } catch (error) {
    console.error('Error extracting SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to extract project document insights'
    };
  }
};

// Get previously extracted SOW insights for a project
export const getSOWInsights = async (projectId) => {
  try {
    // First try to get cached insights from DB (fast, no AI call needed)
    const cached = await apiClient.get(`/projects/${projectId}/sow-insights`);
    if (cached.data?.has_insights && cached.data?.insights) {
      return {
        success: true,
        insights: cached.data.insights,
        source: cached.data.sow_insights?.source_document_id ? { document_id: cached.data.sow_insights.source_document_id } : null
      };
    }

    // No cached insights — trigger extraction (async job) and wait for result
    const response = await extractSOWInsights(projectId);
    if (response.success && response.insights) {
      return {
        success: true,
        insights: response.insights,
        source: response.sow_document || null,
        newly_generated: true
      };
    }

    return {
      success: false,
      error: response.error || 'Failed to extract project document insights'
    };
  } catch (error) {
    console.error('Error getting SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get project document insights'
    };
  }
};

// Get structured data for Prepare phase components from project documents insights
export const getSOWInsightsForPrepare = async (projectId, section) => {
  try {
    // Get the SOW insights
    const insightsResponse = await getSOWInsights(projectId);
    
    if (!insightsResponse.success || !insightsResponse.insights) {
      return {
        success: false,
        error: insightsResponse.error || 'Project document insights not available'
      };
    }
    
    const insights = insightsResponse.insights;
    
    // Map the requested section to the corresponding insights data
    let result = {
      success: true,
      source: insightsResponse.source,
      section: section
    };
    
    switch (section) {
      case 'project_scope':
        result.data = {
          summary: insights.summary || '',
          objectives: extractListFromInsights(insights.requirements, 3), // Top requirements as objectives
          inScope: extractListFromInsights(insights.key_features, 5),
          outOfScope: [] // Not directly available in insights, would need specific extraction
        };
        break;
        
      case 'business_requirements':
        result.data = {
          introduction: `Based on the project documents, the following business requirements have been identified for this SAP implementation project:`,
          functional: formatRequirementsAsList(insights.requirements),
          nonFunctional: [], // Not directly separated in insights
          technical: [] // Not directly separated in insights
        };
        break;
        
      case 'technical_requirements':
        // Technical requirements may need to be extracted from requirements or features
        result.data = {
          overview: `Based on the project documents, the following technical requirements have been identified:`,
          infrastructure: [],
          security: [],
          integration: [],
          configuration: []
        };
        break;
        
      case 'integration_map':
        result.data = {
          overview: `Based on the project documents, the following integration points have been identified:`,
          integrations: [] // Would need specific extraction from requirements or features
        };
        break;
        
      case 'stakeholders':
        result.data = {
          summary: `Based on the project documents, the following stakeholders are involved in this project:`,
          stakeholders: formatStakeholdersFromInsights(insights.stakeholders)
        };
        break;
        
      case 'timeline':
        result.data = {
          overview: `Based on the project documents, the project timeline is as follows:`,
          startDate: extractDateFromTimeline(insights.timeline, 'start'),
          endDate: extractDateFromTimeline(insights.timeline, 'end'),
          phases: extractPhasesFromTimeline(insights.timeline),
          milestones: extractMilestonesFromTimeline(insights.timeline)
        };
        break;
        
      case 'workshop_schedule':
        result.data = {
          introduction: 'Based on the project documents, the following workshops are recommended for this SAP implementation project:',
          workshops: extractWorkshopsFromInsights(insights)
        };
        break;
        
      default:
        // If section is not recognized, return raw insights
        result.data = insights;
    }
    
    return result;
  } catch (error) {
    console.error(`Error getting SOW insights for ${section}:`, error);
    return {
      success: false,
      error: error.message || `Failed to get project document insights for ${section}`
    };
  }
};

// Helper function to extract a list from insights
const extractListFromInsights = (insightList, limit = 0) => {
  if (!insightList || !Array.isArray(insightList)) {
    return [];
  }
  
  if (limit > 0 && insightList.length > limit) {
    return insightList.slice(0, limit);
  }
  
  return insightList;
};

// Helper function to format requirements as a structured list
const formatRequirementsAsList = (requirements) => {
  if (!requirements || !Array.isArray(requirements)) {
    return [];
  }
  
  return requirements.map((req, index) => ({
    id: `REQ-${(index + 1).toString().padStart(3, '0')}`,
    description: req,
    priority: determinePriority(req),
    details: '',
    notes: ''
  }));
};

// Helper function to determine priority based on requirement text
const determinePriority = (reqText) => {
  const lowerText = reqText.toLowerCase();
  if (lowerText.includes('critical') || 
      lowerText.includes('essential') || 
      lowerText.includes('must') || 
      lowerText.includes('mandatory')) {
    return 'High';
  } else if (lowerText.includes('should') || 
             lowerText.includes('important')) {
    return 'Medium';
  }
  return 'Low';
};

// Helper function to format stakeholders from insights
const formatStakeholdersFromInsights = (stakeholders) => {
  if (!stakeholders || !Array.isArray(stakeholders)) {
    return [];
  }
  
  return stakeholders.map((stakeholder, index) => {
    // Extract information using regex if stakeholder is a string
    if (typeof stakeholder === 'string') {
      const name = extractStakeholderInfo(stakeholder, 'name') || `Stakeholder ${index + 1}`;
      const role = extractStakeholderInfo(stakeholder, 'role') || 'Business Owner';
      
      return {
        id: `SH-${(index + 1).toString().padStart(3, '0')}`,
        name: name,
        role: role,
        organization: extractStakeholderInfo(stakeholder, 'organization') || '',
        impact: extractStakeholderInfo(stakeholder, 'impact') || 'Medium',
        influence: extractStakeholderInfo(stakeholder, 'influence') || 'Medium',
        sentiment: extractStakeholderInfo(stakeholder, 'sentiment') || 'Neutral',
        interests: extractStakeholderInfo(stakeholder, 'interests') || '',
        concerns: extractStakeholderInfo(stakeholder, 'concerns') || '',
        engagement: ''
      };
    }
    
    // Return directly if stakeholder is already an object
    return {
      id: `SH-${(index + 1).toString().padStart(3, '0')}`,
      name: stakeholder.name || `Stakeholder ${index + 1}`,
      role: stakeholder.role || 'Business Owner',
      organization: stakeholder.organization || '',
      impact: stakeholder.impact || 'Medium',
      influence: stakeholder.influence || 'Medium',
      sentiment: stakeholder.sentiment || 'Neutral',
      interests: stakeholder.interests || '',
      concerns: stakeholder.concerns || '',
      engagement: stakeholder.engagement || ''
    };
  });
};

// Helper function to extract stakeholder information using regex
const extractStakeholderInfo = (text, field) => {
  const patterns = {
    name: /name:?\s*([^,\.;]+)/i,
    role: /role:?\s*([^,\.;]+)/i,
    organization: /organization:?\s*([^,\.;]+)|department:?\s*([^,\.;]+)/i,
    impact: /impact:?\s*(high|medium|low)/i,
    influence: /influence:?\s*(high|medium|low)/i,
    sentiment: /(positive|neutral|negative)/i,
    interests: /interests:?\s*([^\.;]+)/i,
    concerns: /concerns:?\s*([^\.;]+)/i
  };
  
  if (!patterns[field]) return null;
  
  const match = text.match(patterns[field]);
  if (!match) return null;
  
  if (field === 'impact' || field === 'influence') {
    return match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
  } else if (field === 'sentiment') {
    return match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
  } else if (field === 'organization') {
    return (match[1] || match[2] || '').trim();
  }
  
  return match[1].trim();
};

// Helper function to extract date from timeline
const extractDateFromTimeline = (timeline, type) => {
  if (!timeline || !Array.isArray(timeline)) {
    return new Date();
  }
  
  const datePattern = /(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(\w+\s+\d{1,2},?\s*\d{4})/i;
  
  // Look for start or end date patterns
  const startPatterns = [/start.*date/i, /project.*start/i, /commence/i, /beginning/i];
  const endPatterns = [/end.*date/i, /project.*end/i, /complete/i, /finish/i, /deadline/i];
  
  const patterns = type === 'start' ? startPatterns : endPatterns;
  
  for (const item of timeline) {
    if (typeof item !== 'string') continue;
    
    const matchesPattern = patterns.some(pattern => pattern.test(item));
    if (matchesPattern) {
      const dateMatch = item.match(datePattern);
      if (dateMatch) {
        try {
          return new Date(dateMatch[0]);
        } catch (e) {
          console.warn('Could not parse date:', dateMatch[0]);
        }
      }
    }
  }
  
  // Default date (current date for start, current date + 6 months for end)
  const now = new Date();
  if (type === 'end') {
    now.setMonth(now.getMonth() + 6);
  }
  return now;
};

// Helper function to extract phases from timeline
const extractPhasesFromTimeline = (timeline) => {
  if (!timeline || !Array.isArray(timeline)) {
    return [];
  }
  
  const phases = [];
  const phasePatterns = [/phase/i, /stage/i, /preparation/i, /blueprint/i, /realization/i, /testing/i, /deployment/i, /go[- ]live/i];
  const datePattern = /(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(\w+\s+\d{1,2},?\s*\d{4})/ig;
  
  for (const item of timeline) {
    if (typeof item !== 'string') continue;
    
    const matchesPhase = phasePatterns.some(pattern => pattern.test(item));
    if (matchesPhase) {
      // Extract phase name
      let phaseName = item;
      
      // Try to extract name between beginning of string and first date or colon
      const nameMatch = item.match(/^(.*?)(?=:|\d{1,2}[\/-]|$)/);
      if (nameMatch && nameMatch[1]) {
        phaseName = nameMatch[1].trim();
      }
      
      // Extract dates
      const dates = item.match(datePattern);
      let startDate = null;
      let endDate = null;
      
      if (dates && dates.length >= 2) {
        try {
          startDate = new Date(dates[0]);
          endDate = new Date(dates[1]);
        } catch (e) {
          console.warn('Could not parse dates:', dates);
        }
      } else if (dates && dates.length === 1) {
        try {
          startDate = new Date(dates[0]);
          // Default end date: start date + 1 month
          endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 1);
        } catch (e) {
          console.warn('Could not parse date:', dates[0]);
        }
      }
      
      if (!startDate || !endDate) {
        // If dates couldn't be parsed, create default dates
        startDate = new Date();
        endDate = new Date();
        endDate.setMonth(startDate.getMonth() + 1);
      }
      
      // Get description
      let description = item;
      if (nameMatch && nameMatch[1]) {
        description = item.replace(nameMatch[1], '').trim();
        // Remove leading colon if present
        description = description.replace(/^:\s*/, '');
      }
      
      phases.push({
        id: `PHASE-${phases.length + 1}`,
        name: phaseName,
        startDate: startDate,
        endDate: endDate,
        duration: calculateDuration(startDate, endDate),
        description: description
      });
    }
  }
  
  // If no phases were found, create default SAP implementation phases
  if (phases.length === 0) {
    const startDate = new Date();
    let currentDate = new Date(startDate);
    
    const defaultPhases = [
      { name: 'Project Preparation', duration: 1 },
      { name: 'Business Blueprint', duration: 2 },
      { name: 'Realization', duration: 3 },
      { name: 'Final Preparation', duration: 1.5 },
      { name: 'Go-Live & Support', duration: 1 }
    ];
    
    defaultPhases.forEach((phase, index) => {
      const phaseStartDate = new Date(currentDate);
      const phaseEndDate = new Date(currentDate);
      phaseEndDate.setMonth(phaseEndDate.getMonth() + phase.duration);
      
      phases.push({
        id: `PHASE-${index + 1}`,
        name: phase.name,
        startDate: phaseStartDate,
        endDate: phaseEndDate,
        duration: `${phase.duration} month${phase.duration > 1 ? 's' : ''}`,
        description: `Standard SAP implementation ${phase.name.toLowerCase()} phase`
      });
      
      currentDate = new Date(phaseEndDate);
    });
  }
  
  return phases;
};

// Helper function to extract milestones from timeline
const extractMilestonesFromTimeline = (timeline) => {
  if (!timeline || !Array.isArray(timeline)) {
    return [];
  }
  
  const milestones = [];
  const milestonePatterns = [/milestone/i, /go[- ]live/i, /sign[ -]off/i, /approval/i, /kickoff/i, /delivery/i, /release/i];
  const datePattern = /(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(\w+\s+\d{1,2},?\s*\d{4})/i;
  
  for (const item of timeline) {
    if (typeof item !== 'string') continue;
    
    const matchesMilestone = milestonePatterns.some(pattern => pattern.test(item));
    if (matchesMilestone) {
      // Extract milestone name
      let milestoneName = item;
      
      // Try to extract name between beginning of string and first date or colon
      const nameMatch = item.match(/^(.*?)(?=:|\d{1,2}[\/-]|$)/);
      if (nameMatch && nameMatch[1]) {
        milestoneName = nameMatch[1].trim();
      }
      
      // Extract date
      const dateMatch = item.match(datePattern);
      let milestoneDate = null;
      
      if (dateMatch) {
        try {
          milestoneDate = new Date(dateMatch[0]);
        } catch (e) {
          console.warn('Could not parse date:', dateMatch[0]);
          milestoneDate = new Date();
        }
      } else {
        // If no date was found, use current date
        milestoneDate = new Date();
      }
      
      // Get description and deliverables
      let description = item;
      if (nameMatch && nameMatch[1]) {
        description = item.replace(nameMatch[1], '').trim();
        // Remove leading colon if present
        description = description.replace(/^:\s*/, '');
      }
      
      milestones.push({
        id: `MS-${milestones.length + 1}`,
        name: milestoneName,
        date: milestoneDate,
        type: determineMilestoneType(milestoneName),
        description: description,
        deliverables: extractDeliverables(description)
      });
    }
  }
  
  // If no milestones were found, create default SAP implementation milestones
  if (milestones.length === 0) {
    const startDate = new Date();
    
    const defaultMilestones = [
      { name: 'Project Kickoff', offset: 0, deliverables: 'Project Charter, Initial Project Plan' },
      { name: 'Business Blueprint Sign-off', offset: 2, deliverables: 'Approved Blueprint Document, Process Flows' },
      { name: 'Development Complete', offset: 5, deliverables: 'Configured System, Custom Programs, Unit Test Results' },
      { name: 'User Acceptance Testing Complete', offset: 6.5, deliverables: 'Test Results, Sign-off Documents' },
      { name: 'Go-Live', offset: 8, deliverables: 'Production System, Cutover Plan, Hypercare Support Plan' }
    ];
    
    defaultMilestones.forEach((milestone, index) => {
      const milestoneDate = new Date(startDate);
      milestoneDate.setMonth(milestoneDate.getMonth() + milestone.offset);
      
      milestones.push({
        id: `MS-${index + 1}`,
        name: milestone.name,
        date: milestoneDate,
        type: determineMilestoneType(milestone.name),
        description: `Standard SAP implementation milestone: ${milestone.name}`,
        deliverables: milestone.deliverables
      });
    });
  }
  
  return milestones;
};

// Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate) => {
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffMonths = Math.ceil(diffDays / 30);
  
  if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }
};

// Helper function to determine milestone type
const determineMilestoneType = (milestoneName) => {
  const name = milestoneName.toLowerCase();
  
  if (name.includes('kickoff') || name.includes('start')) {
    return 'Project Start';
  } else if (name.includes('go-live') || name.includes('go live') || name.includes('golive')) {
    return 'Go-Live';
  } else if (name.includes('sign-off') || name.includes('approval') || name.includes('complete')) {
    return 'Approval';
  } else if (name.includes('test') || name.includes('uat')) {
    return 'Testing';
  } else if (name.includes('delivery') || name.includes('deliverable')) {
    return 'Deliverable';
  }
  
  return 'Project Milestone';
};

// Helper function to extract deliverables from description
const extractDeliverables = (description) => {
  if (!description) return '';
  
  // Look for deliverables after "deliverables:" or similar keywords
  const deliverablesMatch = description.match(/deliverables:?\s*([^\.;]+)/i);
  if (deliverablesMatch) {
    return deliverablesMatch[1].trim();
  }
  
  return '';
};

// Helper function to extract workshops from project documents insights
const extractWorkshopsFromInsights = (insights) => {
  const workshops = [];
  const timelineItems = insights.timeline || [];
  const milestones = insights.milestones || [];
  const phases = insights.phases || [];
  
  // Generate workshop dates based on project timeline
  let projectStart = new Date();
  let projectEnd = new Date();
  projectEnd.setMonth(projectEnd.getMonth() + 6);
  
  // Try to extract start and end dates from timeline
  for (const item of timelineItems) {
    if (typeof item !== 'string') continue;
    
    if (item.toLowerCase().includes('start') || item.toLowerCase().includes('kickoff')) {
      const dateMatch = item.match(/(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(\w+\s+\d{1,2},?\s*\d{4})/i);
      if (dateMatch) {
        try {
          projectStart = new Date(dateMatch[0]);
        } catch (e) {
          console.warn('Error parsing project start date:', e);
        }
      }
    } else if (item.toLowerCase().includes('end') || item.toLowerCase().includes('go-live')) {
      const dateMatch = item.match(/(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})|(\w+\s+\d{1,2},?\s*\d{4})/i);
      if (dateMatch) {
        try {
          projectEnd = new Date(dateMatch[0]);
        } catch (e) {
          console.warn('Error parsing project end date:', e);
        }
      }
    }
  }
  
  // Calculate project duration in days
  const projectDuration = Math.ceil((projectEnd - projectStart) / (1000 * 60 * 60 * 24));
  
  // Standard workshops based on SAP methodology
  const standardWorkshops = [
    {
      id: 'WS-001',
      title: 'Project Kickoff Workshop',
      dayOffset: 0,
      duration: 3,
      attendees: [
        { name: 'Project Manager', role: 'Project Manager', organization: '', required: true },
        { name: 'Business Analyst', role: 'Business Analyst', organization: '', required: true },
        { name: 'Technical Lead', role: 'Technical Lead', organization: '', required: true },
        { name: 'Key Stakeholders', role: 'Stakeholder', organization: '', required: true }
      ],
      objective: 'Introduce the project, clarify expectations, and establish communication protocols.',
      agenda: '1. Introduction and welcome\n2. Project overview and objectives\n3. Project approach and methodology\n4. Timeline and key milestones\n5. Team structure and roles\n6. Communication plan\n7. Next steps',
      materials: 'Project charter, project documents, project plan',
      notes: ''
    },
    {
      id: 'WS-002',
      title: 'Business Process Analysis Workshop',
      dayOffset: Math.ceil(projectDuration * 0.1),
      duration: 8,
      attendees: [
        { name: 'Process Owner', role: 'Subject Matter Expert', organization: '', required: true },
        { name: 'Business Analyst', role: 'Business Analyst', organization: '', required: true },
        { name: 'End Users', role: 'End User', organization: '', required: true }
      ],
      objective: 'Document current business processes and pain points to inform the future state design.',
      agenda: '1. As-is process review\n2. Pain points identification\n3. Requirements gathering\n4. Success criteria definition\n5. Initial future state discussion',
      materials: 'Current process documentation, Process maps, Requirements template',
      notes: ''
    },
    {
      id: 'WS-003',
      title: 'Solution Design Workshop',
      dayOffset: Math.ceil(projectDuration * 0.2),
      duration: 8,
      attendees: [
        { name: 'Solution Architect', role: 'Technical Lead', organization: '', required: true },
        { name: 'Process Owner', role: 'Subject Matter Expert', organization: '', required: true },
        { name: 'Business Analyst', role: 'Business Analyst', organization: '', required: true },
        { name: 'Technical Consultant', role: 'Consultant', organization: '', required: true }
      ],
      objective: 'Design the to-be business processes leveraging SAP standard functionality and identifying potential gaps.',
      agenda: '1. To-be process design\n2. SAP standard solution alignment\n3. Gap analysis\n4. Integration points identification\n5. Solution validation',
      materials: 'Business requirements, SAP best practices documentation, Process design templates',
      notes: ''
    },
    {
      id: 'WS-004',
      title: 'Data Migration Workshop',
      dayOffset: Math.ceil(projectDuration * 0.3),
      duration: 6,
      attendees: [
        { name: 'Data Migration Lead', role: 'Technical Lead', organization: '', required: true },
        { name: 'Business Data Owner', role: 'Subject Matter Expert', organization: '', required: true },
        { name: 'Technical Consultant', role: 'Consultant', organization: '', required: true }
      ],
      objective: 'Define data migration strategy, scope, approach, and responsibilities.',
      agenda: '1. Data migration scope\n2. Source systems analysis\n3. Data quality assessment\n4. Migration approach\n5. Mapping and transformation rules\n6. Testing strategy\n7. Cutover planning',
      materials: 'Data migration templates, Data dictionaries, System landscape overview',
      notes: ''
    },
    {
      id: 'WS-005',
      title: 'Testing Strategy Workshop',
      dayOffset: Math.ceil(projectDuration * 0.4),
      duration: 4,
      attendees: [
        { name: 'Testing Lead', role: 'Technical Lead', organization: '', required: true },
        { name: 'Business Analyst', role: 'Business Analyst', organization: '', required: true },
        { name: 'Process Owner', role: 'Subject Matter Expert', organization: '', required: true },
        { name: 'Quality Assurance', role: 'Consultant', organization: '', required: true }
      ],
      objective: 'Define the testing strategy, scope, approach, and responsibilities for all testing phases.',
      agenda: '1. Testing approach and methodology\n2. Test types and scope\n3. Test case development\n4. Test data requirements\n5. Test environment setup\n6. Test execution planning\n7. Defect management process',
      materials: 'Test strategy template, Test case templates, Process documentation',
      notes: ''
    },
    {
      id: 'WS-006',
      title: 'User Acceptance Testing Workshop',
      dayOffset: Math.ceil(projectDuration * 0.6),
      duration: 10,
      attendees: [
        { name: 'UAT Coordinator', role: 'Business Analyst', organization: '', required: true },
        { name: 'End Users', role: 'End User', organization: '', required: true },
        { name: 'Process Owner', role: 'Subject Matter Expert', organization: '', required: true },
        { name: 'Technical Support', role: 'Technical Lead', organization: '', required: true }
      ],
      objective: 'Execute UAT test cases and validate that the system meets business requirements.',
      agenda: '1. UAT introduction and objectives\n2. Test scenario review\n3. System demonstration\n4. Test execution\n5. Defect reporting\n6. Sign-off process',
      materials: 'UAT test cases, Test data, Defect reporting template, User guides',
      notes: ''
    },
    {
      id: 'WS-007',
      title: 'Go-Live Readiness Workshop',
      dayOffset: Math.ceil(projectDuration * 0.9),
      duration: 6,
      attendees: [
        { name: 'Project Manager', role: 'Project Manager', organization: '', required: true },
        { name: 'Business Stakeholders', role: 'Stakeholder', organization: '', required: true },
        { name: 'Technical Lead', role: 'Technical Lead', organization: '', required: true },
        { name: 'Support Team', role: 'Consultant', organization: '', required: true }
      ],
      objective: 'Assess go-live readiness and finalize cutover plan for production implementation.',
      agenda: '1. Readiness assessment review\n2. Open issues and risks\n3. Cutover plan review\n4. Rollback procedures\n5. Post go-live support\n6. Go/No-go decision criteria',
      materials: 'Cutover plan, Issue log, Go-live checklist, Support plan',
      notes: ''
    }
  ];
  
  // Generate workshops with proper dates and times
  for (const workshop of standardWorkshops) {
    const workshopDate = new Date(projectStart);
    workshopDate.setDate(workshopDate.getDate() + workshop.dayOffset);
    
    const startTime = new Date(workshopDate);
    startTime.setHours(9, 0, 0, 0);
    
    const endTime = new Date(workshopDate);
    endTime.setHours(9 + workshop.duration, 0, 0, 0);
    
    workshops.push({
      id: workshop.id,
      title: workshop.title,
      date: workshopDate,
      startTime: startTime,
      endTime: endTime,
      location: 'Virtual / Conference Room',
      attendees: workshop.attendees,
      objective: workshop.objective,
      agenda: workshop.agenda,
      materials: workshop.materials,
      notes: workshop.notes
    });
  }
  
  return workshops;
};

// Extract stakeholders from project documents with enhanced context
export const extractProjectStakeholders = async (projectId) => {
  try {
    // First process all project documents to ensure they're indexed
    const processResult = await processProject(projectId);
    if (!processResult.success) {
      console.warn("Document processing warning:", processResult.error);
      // Continue anyway
    }
    
    // Create a specialized query for stakeholder extraction
    const query = `Based on all available project documents, provide a comprehensive stakeholder analysis for the SAP implementation project.

Start with a summary overview of stakeholder management, then list each key stakeholder or stakeholder group. For each stakeholder, provide:
1. ID (format: SH-001, SH-002, etc.)
2. Name (person or group)
3. Role (Executive Sponsor, Project Sponsor, Business Owner, End User, IT Team, Project Manager, Subject Matter Expert)
4. Organization/department
5. Impact level (High, Medium, Low)
6. Influence level (High, Medium, Low)
7. Sentiment (Positive, Neutral, Negative)
8. Key interests in the project
9. Key concerns about the project
10. Recommended engagement strategy

Format your response in a structured way with clear section labels. Include both individual stakeholders and stakeholder groups where appropriate.`;
    
    // Use the multi-document approach by sending a request to the enhanced endpoint
    const response = await apiClient.post(`/ai/generate-from-sow/${projectId}`, { section: 'stakeholders', query });
    
    // Process response to extract stakeholders
    if (response.data && response.data.success) {
      // Process the response to ensure consistent structure
      const stakeholderData = {
        success: true,
        stakeholders: {
          summary: '',
          stakeholders: []
        },
        rawContent: response.data.content,
        source_documents: response.data.supporting_sources || [],
        primary_source: response.data.primary_source || response.data.sow_document
      };
      
      // Parse the content to extract stakeholders (handled by the component's parsing functions)
      return stakeholderData;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error extracting project stakeholders:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to extract project stakeholders'
    };
  }
};

// Direct query to a document without using vector database (more reliable approach)
export const directDocumentQuery = async (documentId, query) => {
  try {
    const queryString = typeof query === 'string' ? query : String(query);
    const response = await apiClient.post(`/ai/direct-document-query/${documentId}`, { query: queryString });

    // Async path
    if (response.data?.job_id) {
      const result = await _pollJob(response.data.job_id, 'AI Document Query…');
      return result || { success: false, error: 'No result from job' };
    }

    // Legacy sync path
    return response.data;
  } catch (error) {
    console.error('Error querying document directly:', error);
    return { success: false, document_id: documentId, query, error: error.message || 'Failed to query document directly' };
  }
};

// Extract timeline information from project documents using the specialized endpoint
export const extractProjectTimeline = async (projectId) => {
  try {
    const response = await apiClient.post(`/ai/extract-project-timeline/${projectId}`);
    
    // Process the response to ensure proper structure
    if (response.data && response.data.success && response.data.timeline_data) {
      // Process timeline data to handle date formats consistently
      const timeline_data = response.data.timeline_data;
      
      // Ensure timeline overview is a string
      if (timeline_data.timeline_overview && typeof timeline_data.timeline_overview !== 'string') {
        timeline_data.timeline_overview = String(timeline_data.timeline_overview);
      }
      
      // Format phases to ensure consistent structure
      if (timeline_data.phases && Array.isArray(timeline_data.phases)) {
        timeline_data.phases = timeline_data.phases.map(phase => ({
          id: phase.id || `PHASE-${Math.floor(Math.random() * 900) + 100}`,
          name: phase.name || 'Unnamed Phase',
          startDate: phase.start_date || phase.startDate,
          endDate: phase.end_date || phase.endDate,
          duration: phase.duration || '',
          description: phase.description || ''
        }));
      }
      
      // Format milestones to ensure consistent structure
      if (timeline_data.milestones && Array.isArray(timeline_data.milestones)) {
        timeline_data.milestones = timeline_data.milestones.map(milestone => ({
          id: milestone.id || `MS-${Math.floor(Math.random() * 900) + 100}`,
          name: milestone.name || 'Unnamed Milestone',
          date: milestone.date,
          type: milestone.type || 'Project Milestone',
          description: milestone.description || '',
          deliverables: milestone.deliverables || ''
        }));
      }
      
      // Set overview and date fields required by the UI
      return {
        success: true,
        timeline: {
          overview: timeline_data.timeline_overview || '',
          startDate: timeline_data.project_dates?.start_date || new Date(),
          endDate: timeline_data.project_dates?.end_date || new Date(),
          phases: timeline_data.phases || [],
          milestones: timeline_data.milestones || []
        },
        source_documents: response.data.source_documents || [],
        meta: timeline_data.meta || {}
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error extracting project timeline:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to extract project timeline'
    };
  }
};
